import { FC } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { formatBatchLabel } from 'src/constants/lottery/batchLabel';
import { BATCH_STATUS_RUNNING } from 'src/constants/lottery/statusLabels';
import { LotteryActiveBatch } from 'src/types/Lottery';
import LotteryBatchStatusChip from '../chips/LotteryBatchStatusChip';

export interface TrayBatchRowProps {
  batch: LotteryActiveBatch;
}

/** The display step a batch sits at; a terminal row shows a full bar. */
export function trayProgressValue(batch: LotteryActiveBatch): number {
  const count = batch.step_count || 8;
  if (batch.state_group !== 'in_progress') {
    return count;
  }
  return Math.min(Math.max(batch.step_number ?? 0, 0), count);
}

export const TrayBatchRow: FC<TrayBatchRowProps> = ({ batch }) => {
  const stepCount = batch.step_count || 8;
  const progressValue = trayProgressValue(batch);
  const percent = stepCount > 0 ? (progressValue / stepCount) * 100 : 0;
  const runPath = `/workbench/${batch.property.slug}/lottery/${batch.id}`;
  const statusLabel =
    batch.state_group === 'in_progress' ? BATCH_STATUS_RUNNING : undefined;
  const displayLabel = formatBatchLabel(
    batch.batch_label,
    batch.preference_group
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Link
          component={RouterLink}
          to={runPath}
          underline="hover"
          variant="body2"
          sx={{ fontWeight: 600 }}
        >
          {displayLabel}
        </Link>
        <LotteryBatchStatusChip
          stateGroup={batch.state_group}
          state={batch.state}
          label={statusLabel}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {batch.current_step_label}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {`Step ${progressValue} of ${stepCount}`}
        </Typography>
      </Box>
      <Box
        role="progressbar"
        aria-valuenow={progressValue}
        aria-valuemin={0}
        aria-valuemax={stepCount}
        aria-label={`${displayLabel} progress`}
        data-testid={`tray-progressbar-${batch.id}`}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'grey.200',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${percent}%`,
            bgcolor: 'primary.main',
          }}
        />
      </Box>
    </Box>
  );
};

export default TrayBatchRow;
